import React, { Fragment, useContext, useState } from "react"
import { scrollRefIntoView } from "../helpers/helpers"
import { UserContext } from "../Contexts/UserContext/UserContext"
import TxStateContext, { sendtx_status } from "../Contexts/TxStateContext"
import { Loader } from "../components/Loader"
import GlobalErrorContext from "../Contexts/GlobalErrorContext.js"


// this hook is only for normal transaction using sendtx_status, will not work for other status like oracle

// note: good example of use of this hook in slide_mintTokens


/* 	Good for send_tokens, mint_tokens, burn_tokens and other tokens functions.
	  Can be overriden when calling useTxSender,
	  Example: send_bsv doesn't need to update token balance or token history, so override it where needed
	  We use this default func that reloads token balance & history by default if not overriden
*/
export async function reload_token_balance_andHistory(userContext) {
	// by default, to update display let's at least reload the balance of tokens 
	await userContext.get_token_balance() // faster to update display than reload_wallet

	// now that balance display is updated, let's reload the history display
	await userContext.get_tx_history()

	// userContext.reload_wallet() // too slow don't do this here !!!
}


/**
 * The goal of this hook is to extract ALL the reusable logic around displaying
 * the building & broadcasting of actions using RUN smartcontracts
 * 
 * BEWARE DO NOT CALL set_var(txstate.p) functions (from useState hook) in func_jsx_success() or func_jsx_fail()
 * otherwise you'll create an infinite loop:
 * 		useTxSender => func_jsx_success() => set_var(txstate.p) => component refreshes => useTxSender => etc...
 * 
 * @param {*} tokenActionID 
 * @param {*} bool_has_all_user_input 
 * @param {*} bool_context_has_loaded 
 * @param {*} ref_messageEndRef 
 * @param {*} mess_default
 * @param {*} jsx_loading 	// custom loader to display if we have user input & context not loaded yet
 * @param {*} jsx_recap 
 * @param {*} jsx_sending 				jsx or a function that will receive txstate as arg & return jsx
 * @param {*} func_jsx_success 
 * @param {*} func_jsx_fail 
 * @param {*} func_empty_fields 
 * @param {*} func_onSuccess_once (optional) called right after func_empty_fields, will receive as arg: userContext,
 * 									default: reload_token_balance_andHistory
 * @returns [
 * 		bool_broadcast_lock
		func_resetMess
		dict_sendbutton_style
		jsx_mess
	]
 */
export default function useTxSender(
	tokenActionID,
	array_bool_user_inputs,
	bool_context_has_loaded,
	ref_messageEndRef,
	mess_default,
	jsx_loading,
	jsx_recap,
	jsx_sending,
	func_jsx_success,
	func_jsx_fail,
	func_empty_fields,
	func_onSuccess_once = reload_token_balance_andHistory
) {
	const function_id = "useTxSender(" + tokenActionID + "): "
	const log = (...args) => { console.log(function_id, ...args) }

	const userContext = useContext(UserContext)

	const globalErrorContext = useContext(GlobalErrorContext)


	// --- VARS ---

	const [local_txstate_timestamp, set_local_txstate_timestamp] = useState(0)

	const txStateContext = useContext(TxStateContext)
	const txstate = txStateContext.sendtxs_state ? txStateContext.sendtxs_state[tokenActionID] : null
	// (depends on the type) => .status, .timestamp, .hash, .bsvfee, .error, .sendto_address, .amount, .nbtokens...

	const bool_broadcast_lock = Boolean(txstate && txstate.status === sendtx_status.sending)

	const bool_has_all_user_input = array_bool_user_inputs.every(v => v === true)
	const bool_has_some_user_input = array_bool_user_inputs.some(v => v === true)


	// --- FUNCTIONS ---

	const func_resetMess = () => {
		if (local_txstate_timestamp) {
			// if we are already displaying sentfail or sentsuccess message,
			// we increase local timestamp to stop displaying it and move on
			set_local_txstate_timestamp(local_txstate_timestamp + 1) // Date.now() also works
		}
	}


	// --- DISPLAY ---

	var jsx_info = null
	var jsx_error = null

	// if bsvtx.status has been set
	if (txstate && txstate.status != null) {
		log("txstate.status=", txstate.status, "for txstate=", txstate)

		// "tx is sending" mode
		if (txstate.status == sendtx_status.sending) {
			if (typeof jsx_sending !== "function") jsx_info = jsx_sending
			else {
				// we had issue with black screen if this function fails, so let's catch it
				// TODO we should probably catch all similar functions that are from args
				try {
					jsx_info = jsx_sending(txstate)
				} catch (e) {
					globalErrorContext.throwContextError(e, function_id + "mode [tx is sending] ")
				}
			}
		}
		// "just received new txstate" mode
		else if (local_txstate_timestamp < txstate.timestamp) {
			log("Detected new txstate:",
				"\n txstate.status=", txstate.status,
				"\n local_txstate_timestamp = ", local_txstate_timestamp,
				"\n vs txstate.timestamp=", txstate.timestamp)

			if (txstate.status == sendtx_status.sentsuccess) {
				log("broadcast success, txstate.hash=", txstate.hash)

				set_local_txstate_timestamp(txstate.timestamp)
				func_empty_fields()

				func_onSuccess_once(userContext)
			}
			else if (txstate.status == sendtx_status.sentfail) {

				log("failed to send txstate=", txstate)

				set_local_txstate_timestamp(txstate.timestamp)
			}
		}
		// "display last sentfail or sentsuccess" mode
		else if (local_txstate_timestamp == txstate.timestamp) {
			if (txstate.status == sendtx_status.sentsuccess) {
				log("mode [display last sentsuccess] txstate=", txstate)
				jsx_info = func_jsx_success(txstate)
			}
			else if (txstate.status == sendtx_status.sentfail) {
				log("mode [display last sentfail] txstate=", txstate)
				jsx_error = func_jsx_fail(txstate)
			}
		}
	}

	// override by highest priority information: error, then info, then normal
	var jsx_mess = null
	if (jsx_error) {
		jsx_mess = jsx_error
		scrollRefIntoView(ref_messageEndRef) // very important mess
	}
	else if (jsx_info) {
		jsx_mess = jsx_info
		scrollRefIntoView(ref_messageEndRef) // very important mess
	}
	// these following messages are "normal" level
	else if (bool_has_some_user_input && !bool_context_has_loaded) {  // loader
		// no info message until context has loaded because it could be false otherwise
		if (jsx_loading) jsx_mess = jsx_loading
		else jsx_mess = <Loader /> // default loader
	}
	else if (!bool_has_some_user_input) {
		jsx_mess = mess_default
	}
	else { // == if(bool_context_has_loaded && bool_has_all_user_input){
		jsx_mess = jsx_recap
	}


	// --- SEND_BUTTON_STYLE ---

	var txstate_is_ready = (txstate == null || txstate.status == null || txstate.status != sendtx_status.sending)

	// on vérifie si on a tous les éléments pour construire la tx
	var cansendnow = (
		txstate_is_ready
		&& bool_context_has_loaded
		&& bool_has_all_user_input)

	// activate or de-activate the send button
	var dict_sendbutton_style = {}
	if (!cansendnow) {
		dict_sendbutton_style = {
			// pointerEvents: "none !important",
			// backgroundColor: "gray !important",
			pointerEvents: "none",
			backgroundColor: "gray",
		}
	}


	// --- RETURN ---

	log("\n txstate=", txstate,
		"\n txstate.status = ", txstate && txstate.status,
		"\n stateisempty  = ", txstate_is_ready,
		"\n has jsxerror?", jsx_error != null,
		"\n has jsxinfo?", jsx_info != null,
		"\n bool_context_has_loaded=", bool_context_has_loaded,
		"\n bool_has_all_user_input=", bool_has_all_user_input,
		"\n cansendnow =", cansendnow,
		"\n bool_broadcast_lock=", bool_broadcast_lock,
		"\n txStateContext.sendtxs_state=", txStateContext.sendtxs_state)

	return [
		bool_broadcast_lock,
		func_resetMess,
		dict_sendbutton_style,
		jsx_mess,
	]
}
